import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { getCurrentFiles, getFileThumbnail, uploadInstallationStatementFile, deleteFile, getFile } from '../api/files.api'
import { Image, ImagesFormData, InstallationStatementType } from 'src/Libs/installation-statement/models/images'
import { transformGetFilesDtoToModel } from 'src/Libs/installation-statement/transformers/image-data.transformer'

interface UseImagesHook {
  uploadImage: (deviceLinkId: number, installationId: number, file: Blob, imageType: InstallationStatementType) => Promise<number | null>
  getImagePreview: (installationId: number, fileId: number) => Promise<string | null>
  getAllUploadedImages: (deviceLinkId: number, installationId: number) => Promise<ImagesFormData | null>
  deleteImage: (installationId: number, fileId: number) => Promise<boolean>
}

/**
 * A custom hook to provide functionality for the images step of the installation statement.
 * Following functions are included: upload image and get image preview
 */
const useImages = (): UseImagesHook => {
  const authFetch = useAuthFetchWithResponse()

  /**
   * A function to upload an image for an installation statement.
   * @returns number - id of the uploaded file, or -1 if no id was returned
   */
  const uploadImage = async (
    deviceLinkId: number,
    installationId: number,
    file: Blob,
    imageType: InstallationStatementType
  ): Promise<number | null> => {
    const formData = new FormData()
    formData.append('tag', imageType)
    formData.append('file', file)

    const result = await uploadInstallationStatementFile(deviceLinkId, installationId, formData, authFetch)

    if (result.hasErrors && result.error) {
      return null
    }

    return result.value
  }

  const getImagePreview = async (installationId: number, fileId: number): Promise<string | null> => {
    const result = await getFileThumbnail(installationId, fileId, authFetch)

    if (result.hasErrors && result.error) {
      return null
    }

    if (result.value) {
      return window.URL.createObjectURL(new Blob([result.value as string]))
    }

    return null
  }

  const getAllUploadedImageIds = async (deviceLinkId: number, installationId: number): Promise<ImagesFormData | null> => {
    const result = await getCurrentFiles(deviceLinkId, installationId, authFetch)

    if (result.hasErrors && result.error) {
      return null
    }

    if (result.value) {
      return transformGetFilesDtoToModel(deviceLinkId, installationId, result.value)
    }

    return null
  }

  const getImageSizeInfo = async (imageData: ImagesFormData, installationId: number): Promise<ImagesFormData> => {
    const imageIdsWithSize = (
      await Promise.allSettled(Object.values(imageData.images).flatMap(({ id }) => (id ? [getFile(installationId, id, authFetch)] : [])))
    )
      .flatMap(p => (p.status === 'fulfilled' && p.value.value ? [p.value.value] : []))
      .map(({ id, size }) => ({ id, size }))

    const updateSize = (category: InstallationStatementType): Image => ({
      ...imageData.images[category],
      size: imageData.images[category].id ? imageIdsWithSize.find(i => i.id === imageData.images[category].id)?.size : undefined,
    })

    return {
      ...imageData,
      images: (Object.keys(imageData.images) as Array<keyof typeof imageData.images>).reduce((acc, category) => {
        acc[category] = updateSize(category)
        return acc
      }, imageData.images),
    }
  }

  const getAllUploadedImages = async (deviceLinkId: number, installationId: number): Promise<ImagesFormData | null> => {
    const imageData = await getAllUploadedImageIds(deviceLinkId, installationId)

    if (!imageData) {
      return null
    }

    const imageDataWithSize = await getImageSizeInfo(imageData, installationId)

    let key: keyof typeof imageDataWithSize.images
    for (key in imageDataWithSize.images) {
      const { id } = imageDataWithSize.images[key]

      if (id) {
        const image = await getImagePreview(installationId, id)
        if (image) {
          imageDataWithSize.images[key] = { ...imageDataWithSize.images[key], previewUrl: image }
        }
      }
    }

    return imageDataWithSize
  }

  const deleteImage = async (installationId: number, fileId: number): Promise<boolean> => {
    const result = await deleteFile(installationId, fileId, authFetch)

    if (result.hasErrors && result.error) {
      return false
    }

    return !!result.value
  }

  return { uploadImage, getImagePreview, getAllUploadedImages, deleteImage }
}

export default useImages
